import native from 'src/config/native.js'
import sentryConfig from 'src/store/sentry.js'
import { REFRESH_TOKEN_IGNORED_URLS } from 'src/config/constants.ts'
import { hasAppCacheForUrl } from 'src/store/vue-axios/cacheHttp.js'
import Auth from 'src/api/auth.js'
import GlobalPrototypes from 'src/config/services/globalPrototypes.ts'

const messageStack = {
  errors: [],
  warnings: [],
  success: []
}

const messageTypes = {
  warnings: 'warnings',
  errors: 'errors',
  success: 'success'
}

const showMessages = (messages, type) => {
  const message = messages.join('\n')
  if (message.length) {
    GlobalPrototypes.toastNotify({
      type: (type === messageTypes.errors) ? 'error' : type,
      text: message
    })

    native.vibrate()
  }
}

const getWholeRequestUser = (config) => {
  // if getSelf fails or there is no appCache, store.getters wont return anything but we still need the uuid to refresh the token...
  const me = GlobalPrototypes.store.getters['users/me'] || GlobalPrototypes.store.getters['multiUsers/currentStoredUser']
  // ... but the requestUser can still be null if localStorage?.getItem('user_uuid') doesnt exist
  // in this case the user will be getting logged out (all connected users, because we can't decide which is the failing one)
  if (!config.actingAs) return me || (localStorage?.getItem('user_uuid') && { uuid: localStorage?.getItem('user_uuid') })
  const storedUsers = GlobalPrototypes.store.getters['multiUsers/storedUsers']
  return storedUsers && Object.values(storedUsers).find(el => el.uuid === config.actingAs.uuid)
}

const extractUserDataForBreadcrumb = (user) => {
  if (!user) return
  return {
    id: user.id,
    uuid: user.uuid,
    authToken: user.authToken
  }
}

const ignoreRefreshTokenPromiseForUrls = (configUrl, urls) => {
  if (Array.isArray(urls) && urls.length) return urls.some(url => configUrl.includes(url))
  return false
}

export const getErrorForBreadcrumb = (error) => {
  if (typeof btoa === 'undefined') return 'btoa not available'
  try {
    return btoa(JSON.stringify(error))
  } catch {
    return btoa(error)
  }
}

export const handleResponse = (data) => {
  if (!data) return
  Object.entries(data)
    .forEach(([key, value]) => {
      if (!value || !messageTypes[key] || !Array.isArray(value)) return
      messageStack[key] = [...messageStack[key], ...value]
    })
  return true
}

export const notify = (config) => {
  if (config.showMessages) {
    showMessages(messageStack[messageTypes.errors], messageTypes.errors)
    showMessages(messageStack[messageTypes.warnings], messageTypes.warnings)
    // ignore only success messages
    if (!config.hideSuccessMessages) {
      showMessages(messageStack[messageTypes.success], messageTypes.success)
    }
  }
  // dropkick current messages from stack ;)
  // not using .shift() because there can be multiple messages in the stack
  messageStack[messageTypes.errors] = []
  messageStack[messageTypes.warnings] = []
  messageStack[messageTypes.success] = []
  return true
}

export const waitForPendingRefresh = async (config, pendingRefreshingTokenPromise) => {
  // If /auth/refresh is in progress and a parallel api call is executed, /auth/refresh would be called again. Because
  // of this, there can be situations where the auth token gets refreshed two times. The newest auth token is correctly
  // stored in localStorage, but not the newest refresh token. This leads to a chain of 401 calls and the user is getting
  // logged out.
  // Example case:
  // - Click on push notification
  // - Auth token is expired
  // - App init (Vuex init) calls getSelf which fails with 401 and calls refresh
  // - In the same time user gets redirected to chat which also calls API which fails with 401 and calls refresh

  // do not wait the refresh token promise if the url is in the ignore list
  // ignore list consists of urls which do not need to have a valid auth_token
  if (config.bypassRefresh) return
  if (ignoreRefreshTokenPromiseForUrls(config.url, REFRESH_TOKEN_IGNORED_URLS)) return
  if (config.url === Auth.authRefreshUrl(GlobalPrototypes.store.state.isNative) || !pendingRefreshingTokenPromise) return
  const refreshedAuthToken = await pendingRefreshingTokenPromise
  config.headers.Authorization = `Bearer ${refreshedAuthToken.authToken}`
}

/**
 * Sets the authorization header
 * - /auth/refresh never needs it
 * - !actingAs -> the global axios header will be used
 * - actingAs.authToken -> API call for an inactive user
 * @param config
 * @returns {{Authorization: string}|null|{Authorization: null}}
 */
export const getAuthHeader = (config) => {
  // Dont report for auth/refresh if no authToken exists because the refresh can also happen because of
  // the non-existent auth token. In fact the auth/refresh doesn't need an auth token at all.
  if (config.url === Auth.authRefreshUrl(GlobalPrototypes.store.state.isNative)) return { Authorization: null }
  if (!config.actingAs) return null
  if (!config.actingAs.authToken) {
    console.warn('cant get auth header', config.url)
    sentryConfig.addBreadcrumb({
      category: 'actingAs',
      message: getErrorForBreadcrumb(extractUserDataForBreadcrumb(config.actingAs)),
      level: 'warning'
    })
    sentryConfig.addBreadcrumb({
      category: 'config.url',
      message: config.url,
      level: 'warning'
    })
    sentryConfig.throwError('AuthHeader: Cant get auth token')
  }
  return { Authorization: `Bearer ${config.actingAs.authToken}` }
}

/**
 * Tries to determine the requesting user. The requestUser is needed if the api call responds with 401
 * and /auth/refresh needs to happen. It should ONLY return null if we are sure that the api call
 * will never fail with 401 such as when getSelf is called on app init. If the call still fails with 401
 * the user is getting logged out.
 * @param config
 * @returns {Promise<null|{school: {slink}, authToken, id, uuid}>}
 */
export const getRequestUser = async (config) => {
  const user = getWholeRequestUser(config)

  // If there is no auth header and no requestUser the user is either not logged in or getSelf onInit is happening.
  // The first getSelf onInit should never fail because it uses the auth token which got generated right before at login.
  if (!user && !config.headers.Authorization) return null

  // User is either determined by appCache (store) or localStorage
  if (!user || !user.uuid) {
    // Check if we're at a public route. Endpoints don't require a user on these routes, so don't report this to sentry.
    if (!GlobalPrototypes.router.currentRoute.value?.meta?.isPublic) {
      sentryConfig.addBreadcrumb({
        category: 'config.actingAs.uuid',
        message: config.actingAs?.uuid,
        level: 'warning'
      })
      const storedUsers = GlobalPrototypes.store.getters['multiUsers/storedUsers']
      sentryConfig.addBreadcrumb({
        category: 'storedUsers',
        message: storedUsers && !!Object.keys(storedUsers).length ? getErrorForBreadcrumb(storedUsers) : 'null',
        level: 'warning'
      })
      sentryConfig.addBreadcrumb({
        category: 'config.url',
        message: config.url,
        level: 'warning'
      })
      sentryConfig.addBreadcrumb({
        category: 'UUID localstorage',
        message: localStorage?.getItem('user_uuid'),
        level: 'warning'
      })
      const hasAppCache = !!await hasAppCacheForUrl(config.url)
      sentryConfig.addBreadcrumb({
        category: 'has app cache',
        message: `${hasAppCache}`,
        level: 'warning'
      })
      sentryConfig.throwError('RequestUser: Cant find user')
    }
    return null
  }

  const school = (user.school && {
    slink: user.school.slink
  }) || undefined

  return {
    id: user.id,
    uuid: user.uuid,
    authToken: user.authToken,
    school: school
  }
}

export const shouldLogOutUser = (request) => {
  // User is getting logged out if:
  // - auth/refresh responds with 401
  // - !userForRequest?.uuid
  // - !localStorage?.getItem('user_uuid'))
  const uuid = request.requestUser?.uuid || localStorage?.getItem('user_uuid')
  if (request.url !== Auth.authRefreshUrl(GlobalPrototypes.store.state.isNative) && uuid) return false
  if (!uuid) {
    const storedUsers = GlobalPrototypes.store.getters['multiUsers/storedUsers']
    sentryConfig.addBreadcrumb({
      category: 'storedUsers',
      message: storedUsers && !!Object.keys(storedUsers).length ? getErrorForBreadcrumb(storedUsers) : 'null',
      level: 'warning'
    })
    const me = GlobalPrototypes.store.getters['users/me']
    sentryConfig.addBreadcrumb({
      category: 'me',
      message: me && !!Object.keys(me).length ? getErrorForBreadcrumb(extractUserDataForBreadcrumb(me)) : 'null',
      level: 'warning'
    })
    sentryConfig.addBreadcrumb({
      category: 'requestUser.uuid',
      message: request.requestUser?.uuid,
      level: 'warning'
    })
    sentryConfig.addBreadcrumb({
      category: 'localStorage user_uuid',
      message: localStorage?.getItem('user_uuid'),
      level: 'warning'
    })
    sentryConfig.addBreadcrumb({
      category: 'request url',
      message: request.url,
      level: 'warning'
    })
    sentryConfig.throwError('autoLogout no user uuid')
  }
  return true
}

export const schoolIsBeta = config => {
  const user = getWholeRequestUser(config)
  return !!user?.school?.is_beta
}
